import type { AnySceneObjectT, GroupStyleI } from "tweenly"
import { renderStyles } from "tweenly"
import { transformsUnderParent } from "./affineResize.helpers"
import { isDegenerate, measureChildSpace, measureSpace, SpaceT, toSpace } from "./domSpace.helpers"
import { getObjectPositioning } from "./object.helpers"
import type { Point } from "./orientedResize.helpers"
import { roundTo2 } from "./transform.helpers"

/**
 * Where objects dropped under another parent in the objects tree have to sit, and what their
 * own transform has to be, so that they look exactly as they did — measured off the DOM,
 * before anything moves.
 *
 * Summing up the parents' top / left (getAbsoluteTopLeftPosition) only works for parents that
 * are neither turned nor scaled, not animated and have nothing laid out before their children;
 * the spaces measured off the DOM (domSpace.helpers) know all of that.
 *
 * A parent's transform applies to everything inside it, so a child moved under a turned or
 * skewed parent would be drawn turned or skewed with it. Its `matrix` makes up for that (see
 * transformsUnderParent) — its rotation and every other value stay as they are.
 */

export type DropPositionT = { left: number; top: number; transforms: GroupStyleI[] }

/** The object's own CSS transform, as the canvas renders it. */
const cssTransformOf = (object: AnySceneObjectT): string =>
    String((renderStyles(object, "inline") as { transform?: string }).transform ?? "")

/** The middle of the painted box — the middle of the transformed one. */
const centreOf = (element: HTMLElement): Point => {
    const rect = element.getBoundingClientRect()
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
}

/** Where the centre sits in `space` with the element wearing another own transform. */
const centreWith = (element: HTMLElement, transform: string, space: SpaceT): Point => {
    const inline = element.style.transform
    element.style.transform = transform
    const centre = centreOf(element)
    element.style.transform = inline
    return toSpace(space, centre)
}

/**
 * What each object needs under the new parent to look exactly as it does now: its transforms
 * made up for the parents' (transformsUnderParent), and its top / left set so that its centre
 * stays on the same spot. An object that is not on the canvas, or whose old or new space cannot
 * be measured (isDegenerate), is left out — the caller falls back to summing up the parents'
 * top / left.
 *
 * @param objects the moved objects
 * @param objectsById every object, for the parents' transforms
 * @param newParentId the new parent, null for the root
 * @param getElement the element of an object
 * @param newParentElement the new parent's element, or the canvas when moving to the root
 */
export const measureDropPositions = (
    objects: AnySceneObjectT[],
    objectsById: Record<string, AnySceneObjectT>,
    newParentId: string | null,
    getElement: (id: string) => HTMLElement | null | undefined,
    newParentElement: HTMLElement
): Record<string, DropPositionT> => {
    const newSpace = measureChildSpace(newParentElement)
    const positions: Record<string, DropPositionT> = {}
    if (isDegenerate(newSpace)) return positions
    objects.forEach((object) => {
        const element = getElement(object.id)
        const containingBlock = element?.parentElement
        if (!element || !containingBlock) return
        const oldSpace = measureSpace(containingBlock)
        if (isDegenerate(oldSpace)) return
        const centre = centreOf(element)
        const before = toSpace(oldSpace, centre)
        const after = toSpace(newSpace, centre)

        const transforms = transformsUnderParent(
            object.transforms,
            object.parentId,
            newParentId,
            objectsById
        )
        // a new matrix about a transform origin off the centre moves the centre as well
        const shift =
            transforms === object.transforms
                ? { x: 0, y: 0 }
                : (() => {
                      const was = centreWith(element, cssTransformOf(object), oldSpace)
                      const will = centreWith(
                          element,
                          cssTransformOf({ ...object, transforms } as AnySceneObjectT),
                          oldSpace
                      )
                      return { x: will.x - was.x, y: will.y - was.y }
                  })()

        const { left, top } = getObjectPositioning(object)
        positions[object.id] = {
            left: roundTo2(Number(left) + after.x - before.x - shift.x),
            top: roundTo2(Number(top) + after.y - before.y - shift.y),
            transforms,
        }
    })
    return positions
}
